// src/components/deck_subdeck/CreateSubdeckModal.tsx
import React, { useState } from 'react';
import { X, ChevronDown, Plus, Check } from 'lucide-react';
import { SubdeckGroup } from './SubdeckGroupContainer';
import { getIconComponent } from './groupIcons';
import { GROUP_COLORS } from './ManageGroupsModal';
import './CreateSubdeckModal.css';

interface Props {
    type: 'flashcards' | 'apostila';
    groups: SubdeckGroup[];
    deckId: string;
    onClose: () => void;
    onCreate: (name: string, description: string, type: 'flashcards' | 'apostila', groupId?: string | null) => Promise<void>;
}

const CreateSubdeckModal: React.FC<Props> = ({ type, groups, deckId, onClose, onCreate }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Grupo selecionado: null = sem grupo, string = _id do grupo existente, 'new' = criar novo
    const [selectedGroup, setSelectedGroup] = useState<string | null>(
        groups.length > 0 ? groups[0]._id : null
    );
    const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);

    // Form para novo grupo inline
    const [showNewGroupForm, setShowNewGroupForm] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupColor, setNewGroupColor] = useState('#0D3B0E');
    const [creatingGroup, setCreatingGroup] = useState(false);
    const [createdGroup, setCreatedGroup] = useState<SubdeckGroup | null>(null);

    const token = () => localStorage.getItem('accessToken') || '';

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) return;
        setCreatingGroup(true);
        try {
            const r = await fetch(`/api/decks/${deckId}/groups`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({
                    name: newGroupName.trim(),
                    iconType: 'lucide',
                    iconName: 'Layers',
                    color: newGroupColor
                })
            });
            if (r.ok) {
                const g: SubdeckGroup = await r.json();
                setCreatedGroup(g);
                setSelectedGroup(g._id);
                setShowNewGroupForm(false);
                setNewGroupName('');
            }
        } catch { /* ignore */ }
        finally { setCreatingGroup(false); }
    };

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSubmitting(true);
        try {
            const effectiveGroupId = selectedGroup === '__none__' ? null : (createdGroup?._id ?? selectedGroup);
            await onCreate(name.trim(), description.trim(), type, effectiveGroupId);
        } finally {
            setSubmitting(false);
        }
    };

    // Todos os grupos disponíveis (originais + recém criado)
    const allGroups = createdGroup
        ? [...groups.filter(g => g._id !== createdGroup._id), createdGroup]
        : groups;

    const selectedGroupObj = selectedGroup === '__none__'
        ? null
        : allGroups.find(g => g._id === selectedGroup) ?? null;

    const renderGroupIcon = (g: SubdeckGroup) => {
        if (g.iconType === 'image' && g.iconUrl) {
            return <img src={g.iconUrl} alt="" style={{ width: 18, height: 18, objectFit: 'cover', borderRadius: 3 }} />;
        }
        const Ic = getIconComponent(g.iconName || 'Layers');
        return <Ic size={16} color={g.color} />;
    };

    const isApostila = type === 'apostila';
    const accentColor = isApostila ? '#2a4a5a' : '#0D3B0E';

    return (
        <div className="csm-overlay" onClick={onClose}>
            <div className="csm-dialog" onClick={e => e.stopPropagation()}>
                <button className="csm-close" onClick={onClose}><X size={18} /></button>

                <h2 className="csm-title">
                    {isApostila ? 'Nova Apostila' : 'Novo Subdeck de Flashcards'}
                </h2>

                {/* Nome */}
                <input
                    className="csm-input"
                    placeholder="Nome"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter' && !showNewGroupForm) handleSubmit(); }}
                />

                {/* Descrição */}
                <textarea
                    className="csm-textarea"
                    placeholder="Descrição (opcional)"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={2}
                />

                {/* Seleção de grupo */}
                <div className="csm-group-section">
                    <label className="csm-group-label">Grupo</label>

                    {/* Dropdown */}
                    <div className="csm-group-dropdown-wrap">
                        <button
                            className="csm-group-dropdown-btn"
                            onClick={() => { setGroupDropdownOpen(p => !p); setShowNewGroupForm(false); }}
                            type="button"
                            style={{ borderColor: selectedGroupObj ? selectedGroupObj.color + '66' : undefined }}
                        >
                            <span className="csm-group-dropdown-selected">
                                {selectedGroupObj
                                    ? <><span className="csm-group-dropdown-icon">{renderGroupIcon(selectedGroupObj)}</span> {selectedGroupObj.name}</>
                                    : <span style={{ color: 'var(--text-secondary)' }}>📂 Sem grupo</span>
                                }
                            </span>
                            <ChevronDown size={15} style={{ flexShrink: 0, opacity: 0.5 }} />
                        </button>

                        {groupDropdownOpen && (
                            <div className="csm-group-dropdown">
                                {/* Opção sem grupo */}
                                <button
                                    className={`csm-group-option ${selectedGroup === '__none__' ? 'csm-group-option--selected' : ''}`}
                                    onClick={() => { setSelectedGroup('__none__'); setGroupDropdownOpen(false); }}
                                    type="button"
                                >
                                    <span className="csm-group-option-icon">📂</span>
                                    <span className="csm-group-option-name">Sem grupo</span>
                                    {selectedGroup === '__none__' && <Check size={14} style={{ marginLeft: 'auto', color: accentColor }} />}
                                </button>

                                {allGroups.map(g => (
                                    <button
                                        key={g._id}
                                        className={`csm-group-option ${selectedGroup === g._id ? 'csm-group-option--selected' : ''}`}
                                        onClick={() => { setSelectedGroup(g._id); setGroupDropdownOpen(false); }}
                                        type="button"
                                    >
                                        <span className="csm-group-option-icon">{renderGroupIcon(g)}</span>
                                        <span className="csm-group-option-name">{g.name}</span>
                                        {selectedGroup === g._id && <Check size={14} style={{ marginLeft: 'auto', color: g.color }} />}
                                    </button>
                                ))}

                                <div className="csm-group-dropdown-divider" />
                                <button
                                    className="csm-group-option csm-group-option--new"
                                    onClick={() => { setGroupDropdownOpen(false); setShowNewGroupForm(true); }}
                                    type="button"
                                >
                                    <Plus size={14} style={{ color: accentColor }} />
                                    <span className="csm-group-option-name" style={{ color: accentColor }}>Criar novo grupo</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Form inline para novo grupo */}
                    {showNewGroupForm && (
                        <div className="csm-new-group-form">
                            <input
                                className="csm-input csm-input--sm"
                                placeholder="Nome do novo grupo"
                                value={newGroupName}
                                onChange={e => setNewGroupName(e.target.value)}
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') handleCreateGroup(); }}
                            />
                            <div className="csm-new-group-colors">
                                {GROUP_COLORS.slice(0, 12).map(c => (
                                    <button
                                        key={c.hex}
                                        type="button"
                                        title={c.label}
                                        className={`csm-color-dot ${newGroupColor === c.hex ? 'csm-color-dot--selected' : ''}`}
                                        style={{ background: c.hex }}
                                        onClick={() => setNewGroupColor(c.hex)}
                                    />
                                ))}
                            </div>
                            <div className="csm-new-group-actions">
                                <button className="csm-btn-cancel csm-btn-cancel--sm" onClick={() => setShowNewGroupForm(false)} type="button">
                                    Cancelar
                                </button>
                                <button
                                    className="csm-btn-create csm-btn-create--sm"
                                    onClick={handleCreateGroup}
                                    disabled={creatingGroup || !newGroupName.trim()}
                                    type="button"
                                    style={{ background: newGroupColor }}
                                >
                                    {creatingGroup ? 'Criando...' : 'Criar grupo'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="csm-actions">
                    <button className="csm-btn-cancel" onClick={onClose}>Cancelar</button>
                    <button
                        className={`csm-btn-create ${isApostila ? 'csm-btn-create--apostila' : ''}`}
                        onClick={handleSubmit}
                        disabled={submitting || !name.trim()}
                    >
                        {submitting ? 'Criando...' : 'Criar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateSubdeckModal;
